/* One warehouse per coffee cart. Shopify's checkout Function remains authoritative. */
(function () {
  'use strict';
  const names = { RCWHSE: 'Oakland', SEAFORTH: 'Vancouver', COMIDWEST: 'Midwest', COSEATAC: 'Seattle', DUPUYHOU: 'Houston', SHANGHAI: 'Shanghai' };
  const display = key => names[key] || key;
  class WarehouseError extends Error {
    constructor(message, details = {}) { super(message); this.name = 'WarehouseError'; Object.assign(this, details); }
  }
  const unavailable = () => new WarehouseError('We couldn’t verify the warehouse. Please try again or contact us for help.', { code: 'unavailable' });
  function summarize(items) {
    const keys = [...new Set(items.filter(item => item.quantity > 0).map(item => item.warehouse).filter(key => key && key !== '__unknown__'))];
    const unknown = items.some(item => item.quantity > 0 && item.warehouse === '__unknown__');
    return { keys, unknown, blocked: unknown || keys.length > 1 };
  }
  function checkAddition(current, incoming) {
    // Neutral products can be added even while an existing coffee cart needs repair.
    if (incoming.every(item => item.warehouse === '')) return;
    const before = summarize(current);
    const after = summarize(current.concat(incoming));
    if (after.unknown) throw new WarehouseError('We couldn’t verify a coffee’s warehouse. Please contact us before adding it or checking out.', { code: 'unknown' });
    if (before.keys.length > 1) throw new WarehouseError('Your cart contains multiple warehouses. Remove items until only one warehouse remains, then add more coffee.', { code: 'mixed' });
    if (after.keys.length > 1) {
      const existing = before.keys[0];
      const other = after.keys.find(key => key !== existing);
      throw new WarehouseError(existing
        ? `Your cart contains coffee from ${display(existing)}. This coffee ships from ${display(other)}. Please place separate orders for each warehouse.`
        : 'These coffees ship from different warehouses. Please add coffees from one warehouse at a time.', { code: 'conflict', warehouse: existing });
    }
  }
  function payload(body) {
    if (body && typeof body === 'object' && typeof body.entries !== 'function') return body;
    if (typeof body === 'string' && /^\s*\{/.test(body)) return JSON.parse(body);
    const pairs = body && typeof body.entries === 'function' ? body : new URLSearchParams(body || '');
    const data = {}, items = [], updates = {};
    for (const [key, value] of pairs) {
      const item = key.match(/^items\[(\d+)\]\[(id|quantity)\]$/);
      const update = key.match(/^updates\[([^\]]*)\]$/);
      if (item) { items[Number(item[1])] ||= {}; items[Number(item[1])][item[2]] = value; }
      else if (update && update[1]) updates[update[1]] = value;
      else if (key === 'id' || key === 'quantity') data[key] = value;
    }
    if (items.length) data.items = items.filter(Boolean);
    if (Object.keys(updates).length) data.updates = updates;
    return data;
  }
  function additions(kind, body, cart) {
    const data = payload(body);
    let items = [];
    let remaining = cart.items;
    if (kind === 'add') items = Array.isArray(data.items) ? data.items : [data];
    if (kind === 'update' && data.updates && !Array.isArray(data.updates)) {
      remaining = cart.items.filter(item => Number(data.updates[item.key] ?? data.updates[item.id] ?? item.quantity) > 0);
      items = Object.entries(data.updates).filter(([id, quantity]) => Number(quantity) > 0 && !cart.items.some(item => item.key === id || item.id === id)).map(([id, quantity]) => ({ id, quantity }));
    }
    const normalized = items.map(item => ({ id: String(item.id || ''), quantity: Number(item.quantity ?? 1) }));
    if (normalized.some(item => !/^\d+$/.test(item.id) || !Number.isInteger(item.quantity) || item.quantity <= 0)) throw unavailable();
    if (kind === 'add' && !normalized.length) throw unavailable();
    return { items: normalized, remaining };
  }
  function createGuard({ readCart, readVariant, readRepairCart, lock = work => work(), changed = () => {}, failed = () => {}, busy = () => {} }) {
    let pending = Promise.resolve();
    let count = 0;
    function run(kind, body, send, cancelled = () => false) {
      count += 1; busy(count);
      const operation = pending.then(() => lock(async () => {
        if (cancelled()) throw new DOMException('Cancelled', 'AbortError');
        // Removing/updating existing lines must remain possible in an invalid cart.
        if (kind === 'add' || kind === 'update') {
          let cart;
          try { cart = await readCart(); changed(cart); }
          catch (error) {
            failed();
            if (kind !== 'update' || !readRepairCart) throw error;
            cart = await readRepairCart();
          }
          const proposed = additions(kind, body, cart);
          if (proposed.items.length) {
            const variants = await Promise.all(proposed.items.map(async item => ({ ...await readVariant(item.id), quantity: item.quantity })));
            checkAddition(proposed.remaining, variants);
          }
        }
        if (cancelled()) throw new DOMException('Cancelled', 'AbortError');
        try { return await send(); }
        catch (error) {
          if (error.name === 'AbortError' || error instanceof WarehouseError || kind === 'check') throw error;
          throw new WarehouseError('Your cart update could not be confirmed. Please review your cart before adding again.', { code: 'uncertain' });
        }
        finally {
          // A lost response may follow a successful write. Refresh, never retry a mutation.
          try { changed(await readCart()); } catch (_) { failed(); }
        }
      })).finally(() => { count -= 1; busy(count); });
      pending = operation.catch(() => {});
      return operation;
    }
    return { run };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { summarize, checkAddition, payload, additions, createGuard, WarehouseError };
  if (typeof window === 'undefined' || typeof document === 'undefined' || window.RoyalWarehouseCart) return;
  const loader = document.querySelector('[data-rc-warehouse-loader]');
  if (!loader) return;
  const root = loader.dataset.root || '/';
  const cartUrl = loader.dataset.cartUrl || `${root}cart`;
  const nativeFetch = window.fetch.bind(window);
  const variantUrls = new Map();
  let state = null, failed = false, working = 0, dialog, returnFocus, lastTrigger;

  function mutation(url, method) {
    try {
      const value = new URL(url, location.href);
      if (value.origin !== location.origin || String(method || 'GET').toUpperCase() !== 'POST') return null;
      return value.pathname.match(/\/cart\/(add|update|change|clear)(?:\.js)?\/?$/)?.[1] || null;
    } catch (_) { return null; }
  }
  async function read(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await nativeFetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
      if (!response.ok) throw unavailable();
      return response;
    } finally { clearTimeout(timer); }
  }
  async function readCart() {
    const url = new URL(cartUrl, location.origin);
    url.searchParams.set('view', 'rc-warehouse');
    const data = await (await read(url)).json();
    if (data.version !== 1 || !Array.isArray(data.items) || data.items.some(item => typeof item.warehouse !== 'string' || !item.id || !item.key)) throw unavailable();
    return data;
  }
  async function readVariant(id) {
    document.querySelectorAll('[data-rc-warehouse-product-url]').forEach(element => {
      (element.dataset.rcWarehouseVariants || '').split(',').forEach(variantId => variantUrls.set(variantId, element.dataset.rcWarehouseProductUrl));
    });
    let path = variantUrls.get(id);
    if (!path) {
      // Shopify redirects this read-only route to the variant's product URL.
      path = (await read(`${root}variants/${encodeURIComponent(id)}`, { method: 'HEAD' })).url;
    }
    const url = new URL(path, location.origin);
    if (url.origin !== location.origin || !/\/products\/[^/]+\/?$/.test(url.pathname)) throw unavailable();
    variantUrls.set(id, url.pathname);
    url.search = ''; url.searchParams.set('view', 'rc-warehouse'); url.searchParams.set('variant', id);
    const data = await (await read(url)).json();
    const variant = data.version === 1 && data.variants?.find(item => item.id === id);
    if (!variant || typeof variant.warehouse !== 'string') throw unavailable();
    return variant;
  }
  function changed(cart) { state = cart; failed = false; paint(); }
  function markFailed() { failed = true; paint(); }
  function cartError() {
    if (failed || !state) return unavailable();
    const summary = summarize(state.items);
    if (summary.unknown) return new WarehouseError('We couldn’t verify a coffee’s warehouse. Please contact us before checkout.', { code: 'unknown' });
    if (summary.keys.length > 1) return new WarehouseError('Your cart contains multiple warehouses. Remove items until only one warehouse remains to continue to checkout.', { code: 'mixed' });
    return null;
  }
  function paint() {
    if (!document.body || (!state && !failed)) return;
    const error = cartError();
    const summary = state && summarize(state.items);
    const message = error ? error.message : summary.keys.length ? `Shipping from ${display(summary.keys[0])}` : '';
    document.querySelectorAll('[data-rc-warehouse-notice]').forEach(element => {
      const text = element.querySelector('[data-rc-warehouse-notice-text]');
      if (text.textContent !== message) text.textContent = message;
      element.hidden = !message;
      element.classList.toggle('rc-warehouse-notice--error', !!error);
      const link = element.querySelector('[data-rc-warehouse-notice-link]');
      link.hidden = !error;
      const contact = error?.code === 'unknown';
      link.href = contact ? `${root}pages/contact` : cartUrl;
      const label = contact ? 'Contact us' : 'Review cart';
      if (link.textContent !== label) link.textContent = label;
    });
    document.querySelectorAll('[name="checkout"]').forEach(button => {
      const block = !!error || working > 0;
      if (block && !button.hasAttribute('data-rc-warehouse-disabled')) {
        button.dataset.rcWarehousePreviousDisabled = String(button.disabled);
        button.setAttribute('data-rc-warehouse-disabled', '');
      }
      if (block) button.disabled = true;
      else if (button.hasAttribute('data-rc-warehouse-disabled')) {
        button.disabled = button.dataset.rcWarehousePreviousDisabled === 'true';
        button.removeAttribute('data-rc-warehouse-disabled');
        delete button.dataset.rcWarehousePreviousDisabled;
      }
    });
  }
  function showError(error) {
    if (error.name === 'AbortError') return;
    if (!document.body) { document.addEventListener('DOMContentLoaded', () => showError(error), { once: true }); return; }
    if (!(error instanceof WarehouseError)) error = error.name === 'SubscriptionCartError' ? new WarehouseError(error.message) : unavailable();
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.className = 'rc-warehouse-dialog';
      dialog.setAttribute('aria-labelledby', 'rc-warehouse-dialog-title');
      dialog.setAttribute('aria-describedby', 'rc-warehouse-dialog-description');
      dialog.innerHTML = '<button type="button" class="rc-warehouse-dialog__close" aria-label="Close warehouse message">×</button><h2 id="rc-warehouse-dialog-title">One warehouse per order</h2><p id="rc-warehouse-dialog-description"></p><div class="rc-warehouse-dialog__actions"><a class="button-primary" data-rc-warehouse-review>View cart</a><a class="button-secondary" data-rc-warehouse-shop></a></div>';
      document.body.appendChild(dialog);
      dialog.querySelector('button').addEventListener('click', () => dialog.close());
      dialog.addEventListener('keydown', event => { if (event.key === 'Escape') event.stopPropagation(); });
      dialog.addEventListener('close', () => { if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true }); });
    }
    dialog.querySelector('h2').textContent = error.code === 'conflict' || error.code === 'mixed' ? 'One warehouse per order' : 'Please check your cart';
    dialog.querySelector('p').textContent = error.message;
    const review = dialog.querySelector('[data-rc-warehouse-review]');
    review.href = error.code === 'unknown' ? `${root}pages/contact` : cartUrl;
    review.textContent = error.code === 'unknown' ? 'Contact us' : 'View cart';
    const shop = dialog.querySelector('[data-rc-warehouse-shop]');
    shop.hidden = !error.warehouse;
    if (error.warehouse) {
      const url = new URL(`${root}collections/all-coffees`, location.origin);
      url.searchParams.set('filter.p.m.custom.warehouse', display(error.warehouse));
      shop.href = url.pathname + url.search;
      shop.textContent = `Continue shopping ${display(error.warehouse)}`;
    }
    if (!dialog.open) { returnFocus = lastTrigger?.isConnected ? lastTrigger : document.activeElement; dialog.showModal(); }
  }
  const lock = work => navigator.locks ? navigator.locks.request('rc-coffee-cart', { mode: 'exclusive' }, work) : work();
  const readRepairCart = async () => {
    const data = await (await read(`${cartUrl}.js`)).json();
    if (!Array.isArray(data.items)) throw unavailable();
    return { items: data.items.map(item => ({ ...item, id: String(item.variant_id), warehouse: '__unknown__' })) };
  };
  const guard = createGuard({ readCart, readVariant, readRepairCart, lock, changed, failed: markFailed, busy: count => { working = count; paint(); } });
  function rejected(error) {
    const issue = error instanceof WarehouseError ? error : unavailable();
    showError(issue);
    return { status: 422, message: 'Cart Error', description: issue.message, rcWarehouseCode: issue.code, rcWarehouse: issue.warehouse };
  }
  async function bodyOf(input, options) {
    if (options?.body !== undefined) return options.body;
    if (!(input instanceof Request)) return '';
    const request = input.clone();
    return request.headers.get('content-type')?.includes('multipart/form-data') ? request.formData() : request.text();
  }
  window.fetch = async function (input, options) {
    const kind = mutation(input instanceof Request ? input.url : input, options?.method || (input instanceof Request ? input.method : 'GET'));
    if (!kind) return nativeFetch(input, options);
    const signal = options?.signal || (input instanceof Request ? input.signal : null);
    try {
      return await guard.run(kind, await bodyOf(input, options), () => nativeFetch(input, options), () => !!signal?.aborted);
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      return new Response(JSON.stringify(rejected(error)), { status: 422, headers: { 'Content-Type': 'application/json' } });
    }
  };

  // Empire and Wishlist Plus also use XHR. Delay only Shopify cart writes;
  // preserve native success/error handlers, response types and all other URLs.
  const xhr = XMLHttpRequest.prototype;
  const nativeOpen = xhr.open, nativeSend = xhr.send, nativeAbort = xhr.abort;
  const requests = new WeakMap();
  xhr.open = function (method, url, async = true, ...rest) {
    if (requests.get(this)?.synthetic) {
      ['readyState', 'status', 'statusText', 'responseText', 'response', 'getAllResponseHeaders', 'getResponseHeader'].forEach(key => { delete this[key]; });
    }
    requests.set(this, { kind: mutation(url, method), async, aborted: false });
    return nativeOpen.call(this, method, url, async, ...rest);
  };
  xhr.abort = function () { const request = requests.get(this); if (request) request.aborted = true; return nativeAbort.call(this); };
  xhr.send = function (body) {
    const request = requests.get(this);
    if (!request?.kind) return nativeSend.call(this, body);
    const target = this;
    const send = () => new Promise((resolve, reject) => {
      target.addEventListener('loadend', resolve, { once: true });
      try { nativeSend.call(target, body); } catch (error) { reject(error); }
    });
    const operation = request.async ? guard.run(request.kind, body, send, () => request.aborted) : Promise.reject(unavailable());
    operation.catch(error => {
      if (request.aborted || error.name === 'AbortError') return;
      const data = rejected(error), text = JSON.stringify(data);
      request.synthetic = true;
      const values = { readyState: 4, status: 422, statusText: 'Unprocessable Content', responseText: text, response: target.responseType === 'json' ? data : text };
      Object.entries(values).forEach(([key, value]) => Object.defineProperty(target, key, { configurable: true, value }));
      Object.defineProperty(target, 'getAllResponseHeaders', { configurable: true, value: () => 'content-type: application/json\r\n' });
      Object.defineProperty(target, 'getResponseHeader', { configurable: true, value: key => key.toLowerCase() === 'content-type' ? 'application/json' : null });
      target.dispatchEvent(new Event('readystatechange'));
      target.dispatchEvent(new ProgressEvent('load'));
      target.dispatchEvent(new ProgressEvent('loadend'));
    });
  };

  const checkoutForms = new WeakSet();
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = event.submitter?.hasAttribute('formaction') ? event.submitter.formAction : form.action;
    if (mutation(action, form.method) === 'add') {
      event.preventDefault(); event.stopImmediatePropagation();
      const body = new FormData(form);
      const button = event.submitter;
      if (button?.disabled) return;
      if (button) lastTrigger = button;
      if (button) button.disabled = true;
      window.fetch(`${root}cart/add.js`, { method: 'POST', body, headers: { Accept: 'application/json' } }).then(async response => {
        const data = await response.json();
        if (!response.ok) { showError(new WarehouseError(typeof data.description === 'string' ? data.description : 'This item could not be added. Please check its availability and quantity.', { code: data.rcWarehouseCode, warehouse: data.rcWarehouse })); return; }
        form.dispatchEvent(new CustomEvent('product-form:submit:success', { bubbles: true, detail: { form: body, response: data, cart: state } }));
        if (loader.dataset.cartRedirection === 'true' || !window.RoyalMiniCart?.enabled) location.assign(cartUrl);
        else window.RoyalMiniCart.cartAdded(state);
      }).catch(showError).finally(() => { if (button) button.disabled = false; });
      return;
    }
    if (event.submitter?.name !== 'checkout' && !/\/checkout\/?$/.test(new URL(action).pathname)) return;
    if (checkoutForms.has(form)) { checkoutForms.delete(form); return; }
    event.preventDefault(); event.stopImmediatePropagation();
    const submitter = event.submitter;
    guard.run('check', null, async () => {
      await window.RoyalSubscriptionCart?.checkCheckout();
      changed(await readCart());
      const error = cartError();
      if (error) throw error;
    }).then(() => { checkoutForms.add(form); form.requestSubmit(submitter); }).catch(showError);
  }, true);
  document.addEventListener('click', event => {
    if (!dialog?.open) lastTrigger = event.target.closest('button, a, input');
    const link = event.target.closest('a[href]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const url = new URL(link.href, location.origin);
    if (url.origin !== location.origin || !/\/checkout\/?$/.test(url.pathname)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    guard.run('check', null, async () => { await window.RoyalSubscriptionCart?.checkCheckout(); changed(await readCart()); const error = cartError(); if (error) throw error; }).then(() => location.assign(url.href)).catch(showError);
  }, true);
  const refresh = () => readCart().then(changed).catch(markFailed);
  const observer = new MutationObserver(paint);
  window.addEventListener('pagehide', () => observer.disconnect());
  window.addEventListener('pageshow', event => { if (event.persisted) { observer.observe(document.body, { childList: true, subtree: true }); refresh(); } });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') refresh(); });
  window.RoyalWarehouseCart = { refresh, showError: message => showError(new WarehouseError(message)) };
  const ready = () => { observer.observe(document.body, { childList: true, subtree: true }); refresh(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, { once: true });
  else ready();
})();
